import { toast, useConfirmation } from '@approvalmax/ui/src/components';
import { miscHelpers } from '@approvalmax/utils';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDebounce } from 'react-use';
import {
    ApiAmaxPayLegalEntityType,
    ApiAmaxPayPaymentDetailsAccountVerificationStatus,
    ApiAmaxPayPaymentDetailsVerificationStatus,
    ApiTypes,
    CountryIsoCode,
    CurrencyIsoCode,
    PaymentDetailsLegalEntityType,
    PaymentDetailsRequirementsItem,
    useCreatePaymentDetails,
    useGetXeroCounterparties,
    useUpdatePaymentDetails,
    useValidatePaymentDetails,
    useVerifyPaymentDetails,
    VerifyPaymentDetails,
} from 'shared/data/webApp/v2';
import { convertAlpha3ToAlpha2CountryCode } from 'shared/helpers';

import {
    countriesForConfirmationOfPayee,
    currenciesForConfirmationOfPayee,
    incorrectAccountNameCode,
} from './EditCounterpartyPaymentDetailsPopupContent.constants';
import {
    checkAreFieldsForConfirmationOfPayeeFilled,
    checkIsChapsPaymentMethod,
    checkIsFasterPaymentsMethod,
    getPaymentDetailsTransferData,
} from './EditCounterpartyPaymentDetailsPopupContent.helpers';
import { messages } from './EditCounterpartyPaymentDetailsPopupContent.messages';
import {
    BenificaryNameVariant,
    ContactQueryData,
    FormValues,
    UseInitialFormValuesParams,
} from './EditCounterpartyPaymentDetailsPopupContent.types';

export const useInitialFormValues = (options: UseInitialFormValuesParams): Partial<FormValues> => {
    const { contact, paymentDetailsId, paymentDetails, fixedFormValues, isConfirmationOfPayeeOnly } = options;

    return useMemo(() => {
        const data = paymentDetails?.find(({ id }) => id === paymentDetailsId);

        const contactCountry = contact?.billingAddress?.country;
        const defaultCountry =
            contactCountry?.length === 3
                ? miscHelpers.castToEnum(CountryIsoCode, convertAlpha3ToAlpha2CountryCode(contactCountry))
                : miscHelpers.castToEnum(CountryIsoCode, contactCountry);

        if (!data) {
            return {
                isDefault: (paymentDetails ?? []).length === 0,
                payeeBankCountry: isConfirmationOfPayeeOnly ? countriesForConfirmationOfPayee[0].id : defaultCountry,
                payerBankCountry: isConfirmationOfPayeeOnly ? countriesForConfirmationOfPayee[0].id : defaultCountry,
                currencyCode: isConfirmationOfPayeeOnly
                    ? currenciesForConfirmationOfPayee[0].id
                    : contact?.financialDetails.currency,
                // this expected to be in GBP so far, since open banking is available only for UK
                // so, all bills are in GBP
                ...fixedFormValues,
            };
        }

        return {
            ...data.bankAccountDetails,
            isDefault: data.isDefault,
            payeeBankCountry: data.bankAccountDetails.bankAccount.countryIsoCode,
            payerBankCountry: miscHelpers.castToEnum(
                CountryIsoCode,
                data.bankAccountDetails.legalEntity.addressInfo?.country
            ),
            currencyCode: data.bankAccountDetails.bankAccount.currencyIsoCode,
            paymentMethod: data.payNetworkName,
            legalEntity: {
                ...data.bankAccountDetails.legalEntity,
                legalEntityType:
                    data.bankAccountDetails.legalEntity.legalEntityType === ApiAmaxPayLegalEntityType.company
                        ? PaymentDetailsLegalEntityType.Company
                        : PaymentDetailsLegalEntityType.Individual,
                ...fixedFormValues?.legalEntity,
            },
            ...fixedFormValues,
        };
    }, [paymentDetails, paymentDetailsId, contact, isConfirmationOfPayeeOnly, fixedFormValues]);
};

export const useFormSteps = (form: UseFormReturn<FormValues>) => {
    const [currencyCode, payerBankCountry, payeeBankCountry, paymentMethod, legalEntityType] = form.watch([
        'currencyCode',
        'payerBankCountry',
        'payeeBankCountry',
        'paymentMethod',
        'legalEntity.legalEntityType',
    ]);

    // Reset values from step 2 and 3 when base fields(currencyCode, payerBankCountry, payeeBankCountry) are changed
    useEffect(() => {
        const currentValues = form.getValues();

        form.reset({ isDefault: currentValues.isDefault, currencyCode, payerBankCountry, payeeBankCountry });
    }, [form, currencyCode, payerBankCountry, payeeBankCountry]);

    // Reset values from step 3 when values from step 2(paymentMethod, legalEntityType) are changed
    useEffect(() => {
        const currentValues = form.getValues();

        form.reset({
            isDefault: currentValues.isDefault,
            currencyCode: currentValues.currencyCode,
            payerBankCountry: currentValues.payerBankCountry,
            payeeBankCountry: currentValues.payeeBankCountry,
            paymentMethod,
            legalEntity: {
                legalEntityType,
            },
        });
    }, [form, paymentMethod, legalEntityType]);

    return {
        isSecondStepVisible: Boolean(currencyCode && payerBankCountry && payeeBankCountry),
        isThirdStepVisible: Boolean(paymentMethod && legalEntityType),
    };
};

export const useFormSubmit = (options: {
    form: UseFormReturn<FormValues>;
    companyId: string;
    counterpartyId?: string;
    paymentDetailsId?: string | null;
    paymentDetailsRequirements?: PaymentDetailsRequirementsItem[];
    checkConfirmationOfPayeeData: () => boolean;
    confirmationOfPayeeData?: VerifyPaymentDetails['response'];
    onPaymentDetailsChanged: (paymentDetails?: ApiTypes['ApiAmaxPayPaymentDetailsCamelCased']) => void;
}) => {
    const {
        form,
        companyId,
        counterpartyId,
        paymentDetailsId,
        paymentDetailsRequirements,
        checkConfirmationOfPayeeData,
        confirmationOfPayeeData,
        onPaymentDetailsChanged,
    } = options;

    const { mutateAsync: validatePaymentDetailsAsync, isLoading: isLoadingValidatePaymentDetailsAsync } =
        useValidatePaymentDetails();
    const { mutate: createPaymentDetails, isLoading: isLoadingCreatePaymentDetails } = useCreatePaymentDetails({
        onSuccess: (data) => {
            onPaymentDetailsChanged(data);
        },
    });
    const { mutate: updatePaymentDetails, isLoading: isLoadingUpdatePaymentDetails } = useUpdatePaymentDetails({
        onSuccess: (data) => {
            onPaymentDetailsChanged(data);
        },
    });

    const { confirmation } = useConfirmation({
        confirmTitle: messages.confirmTitle,
        confirmMessage: messages.confirmDescription,
        confirmButtonMessage: messages.confirmButtonMessage,
        // to prevent select opening after confirmation
        returnFocus: false,
    });

    const handleCreateOrUpdatePaymentDetails = useCallback(
        (values: FormValues) => {
            const expectedVerificationStatus =
                values.selectedBenificaryName === BenificaryNameVariant.Suggested
                    ? ApiAmaxPayPaymentDetailsVerificationStatus.full_match
                    : confirmationOfPayeeData?.status;

            if (paymentDetailsId) {
                updatePaymentDetails({
                    params: {
                        path: {
                            companyId,
                            counterpartyId,
                            paymentDetailsId,
                        },
                    },
                    body: {
                        bankAccountDetails: getPaymentDetailsTransferData(values, paymentDetailsRequirements),
                        isDefault: values.isDefault,
                        expectedVerificationStatus:
                            miscHelpers.castToEnum(
                                ApiAmaxPayPaymentDetailsVerificationStatus,
                                expectedVerificationStatus
                            ) ?? ApiAmaxPayPaymentDetailsVerificationStatus.not_checked,
                    },
                });
            } else {
                createPaymentDetails({
                    params: {
                        path: {
                            companyId,
                            counterpartyId,
                        },
                    },
                    body: {
                        bankAccountDetails: getPaymentDetailsTransferData(values, paymentDetailsRequirements),
                        isDefault: values.isDefault,
                        expectedVerificationStatus:
                            miscHelpers.castToEnum(
                                ApiAmaxPayPaymentDetailsVerificationStatus,
                                expectedVerificationStatus
                            ) ?? ApiAmaxPayPaymentDetailsVerificationStatus.not_checked,
                    },
                });
            }
        },
        [
            paymentDetailsId,
            updatePaymentDetails,
            createPaymentDetails,
            companyId,
            counterpartyId,
            paymentDetailsRequirements,
            confirmationOfPayeeData,
        ]
    );

    const handleSubmit = form.handleSubmit(async (values) => {
        if (!checkConfirmationOfPayeeData()) {
            return;
        }

        try {
            const validationResult = await validatePaymentDetailsAsync({
                params: {
                    path: {
                        companyId,
                    },
                },
                body: getPaymentDetailsTransferData(values, paymentDetailsRequirements),
            });

            if (validationResult?.validationErrors?.length) {
                throw new Error();
            }
        } catch {
            toast.error(messages.validationError);

            return;
        }

        const formValues = { ...values };

        const isSuggestedNameSelected =
            values.selectedBenificaryName === BenificaryNameVariant.Suggested && confirmationOfPayeeData?.actualName;

        if (isSuggestedNameSelected) {
            if (values.legalEntity.legalEntityType === PaymentDetailsLegalEntityType.Company) {
                set(formValues, 'legalEntity.companyName', confirmationOfPayeeData.actualName);
            } else {
                const splittedName = confirmationOfPayeeData.actualName.split(' ');

                set(formValues, 'legalEntity.firstName', splittedName[0]);
                set(formValues, 'legalEntity.lastName', splittedName[1]);
            }
        }

        if (
            confirmationOfPayeeData &&
            confirmationOfPayeeData.status !== ApiAmaxPayPaymentDetailsAccountVerificationStatus.full_match &&
            !isSuggestedNameSelected
        ) {
            confirmation()
                .then(() => {
                    handleCreateOrUpdatePaymentDetails(formValues);
                })
                .catch(() => {});
        } else {
            handleCreateOrUpdatePaymentDetails(formValues);
        }
    });

    const isLoading =
        isLoadingValidatePaymentDetailsAsync ||
        isLoadingCreatePaymentDetails ||
        isLoadingUpdatePaymentDetails ||
        form.formState.isSubmitting;

    return { handleSubmit, isLoading };
};

export const useConfirmationOfPayee = (options: {
    form: UseFormReturn<FormValues>;
    companyId: string;
    paymentDetailsRequirements?: PaymentDetailsRequirementsItem[];
}) => {
    const { form, companyId, paymentDetailsRequirements } = options;

    const { data, mutate, isLoading, reset } = useVerifyPaymentDetails();

    const [
        paymentMethod,
        legalEntity,
        payeeBankCountry,
        currencyCode,
        bankAccountName,
        bankAccountNumber,
        sortCode,
        companyName,
        firstName,
        lastName,
    ] = form.watch([
        'paymentMethod',
        'legalEntity',
        'payeeBankCountry',
        'currencyCode',
        'bankAccount.bankAccountName',
        'bankAccount.routing.bankAccountNumber',
        'bankAccount.routing.sortCode',
        'legalEntity.companyName',
        'legalEntity.firstName',
        'legalEntity.lastName',
    ]);

    const [verifyQueryData, setVerifyQueryData] = useState<ReturnType<typeof getPaymentDetailsTransferData> | null>(
        null
    );
    const [showIncorrectInfo, setShowIncorrectInfo] = useState(false);
    const [showIncorrectName, setShowIncorrectName] = useState(false);

    const neededRequirements = useMemo(
        () =>
            paymentDetailsRequirements?.find(
                (item) =>
                    item.payNetworkName === paymentMethod &&
                    item.legalEntityTypeField.legalEntityType === legalEntity?.legalEntityType
            ),
        [paymentDetailsRequirements, paymentMethod, legalEntity?.legalEntityType]
    );

    const isConfirmationOfPayeeAvailable = useMemo(() => {
        if (!neededRequirements) {
            return false;
        }

        const isSuitablePaymentMethod =
            checkIsFasterPaymentsMethod(neededRequirements.payNetworkName) ||
            checkIsChapsPaymentMethod(neededRequirements.payNetworkName);

        return (
            isSuitablePaymentMethod && payeeBankCountry === CountryIsoCode.GB && currencyCode === CurrencyIsoCode.GBP
        );
    }, [neededRequirements, payeeBankCountry, currencyCode]);

    useDebounce(
        async () => {
            if (!isConfirmationOfPayeeAvailable || showIncorrectName) {
                return;
            }

            const formValues = form.getValues();

            const areFieldsFilled = checkAreFieldsForConfirmationOfPayeeFilled(formValues, neededRequirements);

            if (!areFieldsFilled) {
                reset();

                setVerifyQueryData(null);

                return;
            }

            setVerifyQueryData((current) => {
                // we need to clone data because of the same references after form.getValues()
                const newData = cloneDeep(
                    getPaymentDetailsTransferData(
                        {
                            ...formValues,
                            bankAccount: {
                                ...formValues.bankAccount,
                                // This field is not needed for Confirmation of payee and can be any or empty
                                // But this field is required by payment details model
                                // So we need to set some valid value
                                bankAccountName:
                                    checkIsFasterPaymentsMethod(formValues.paymentMethod) &&
                                    formValues.legalEntity.legalEntityType === PaymentDetailsLegalEntityType.Company
                                        ? formValues.bankAccount.bankAccountName
                                        : 'Confirmation of payee',
                            },
                        },
                        paymentDetailsRequirements
                    )
                );

                if (isEqual(current, newData)) {
                    return current;
                }

                return newData;
            });
        },
        1500,
        [
            isConfirmationOfPayeeAvailable,
            bankAccountName,
            bankAccountNumber,
            sortCode,
            companyName,
            firstName,
            lastName,
            showIncorrectName,
        ]
    );

    useEffect(() => {
        reset();

        if (!verifyQueryData) {
            return;
        }

        mutate({
            params: {
                path: {
                    companyId,
                },
            },
            body: verifyQueryData,
        });
    }, [verifyQueryData, mutate, companyId, reset]);

    const checkConfirmationOfPayeeData = useCallback(() => {
        if (!isConfirmationOfPayeeAvailable || showIncorrectInfo || showIncorrectName) {
            return true;
        }

        if (data?.reasonCode === incorrectAccountNameCode) {
            setShowIncorrectName(true);

            return false;
        }

        if (data?.status === ApiAmaxPayPaymentDetailsAccountVerificationStatus.close_match) {
            setShowIncorrectInfo(true);

            return false;
        }

        return true;
    }, [data, isConfirmationOfPayeeAvailable, showIncorrectInfo, showIncorrectName]);

    const resetConfirmationOfPayeeStates = useCallback(() => {
        setShowIncorrectInfo(false);
        setShowIncorrectName(false);
    }, []);

    const notVerifiedPayee = isConfirmationOfPayeeAvailable ? !data?.status : false;

    return {
        isConfirmationOfPayeeAvailable,
        data,
        isLoading,
        notVerifiedPayee,
        checkConfirmationOfPayeeData,
        showIncorrectInfo,
        showIncorrectName,
        resetConfirmationOfPayeeStates,
    };
};

export const useContactFromQuery = (contact: ContactQueryData | undefined, companyId: string) => {
    const { data } = useGetXeroCounterparties(
        {
            path: {
                companyId,
            },
            query: {
                offset: 0,
                limit: 1,
                nameFilter: contact?.name ?? null,
            },
        },
        {
            enabled: Boolean(contact?.id),
        }
    );

    if (!contact) {
        return null;
    }

    return data?.items[0]?.counterpartyId === contact.id ? data?.items[0] : null;
};
